// Whether the configured API endpoint can actually be reached from the page
// that is loading it.
//
// WHY THIS EXISTS. A panel served over https that points at an http endpoint
// does not fail at build time, does not warn at boot and does not log anything
// the app can see: the browser blocks every request as mixed content and the
// screen just sits there. The only trace is a console line nobody is looking at
// while the login spinner turns. `VITE_API_ENDPOINT` is baked into the bundle,
// so the mistake is made once at build time and discovered once per user.
//
// So the check happens at boot, where the missing-variable check already lives,
// and it fails loudly for the same reason that one does.
//
// PURE AND TESTABLE ON PURPOSE. The page's protocol arrives as an argument
// instead of being read off `window`, so the rule can be exercised without a
// browser — which is the only way the rule gets exercised at all.

// Origins the browser treats as potentially trustworthy even over http, so
// pointing an https page at them is NOT blocked. Without this, somebody running
// a local https dev server against a local backend would get a boot error for
// a setup that works.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]', '::1'];

function hostOf(endpoint) {
    // Not `new URL()`: the endpoint may be relative, and this has to answer for
    // absolute ones only. Everything after `http://` up to the first `/`, `?`
    // or `#`, minus any credentials and port.
    const after = endpoint.slice('http://'.length);
    const authority = after.split(/[/?#]/, 1)[0];
    const hostAndPort = authority.split('@').pop();
    // An IPv6 literal keeps its brackets; anything else drops the port.
    return hostAndPort.startsWith('[')
        ? hostAndPort.slice(0, hostAndPort.indexOf(']') + 1).toLowerCase()
        : hostAndPort.split(':')[0].toLowerCase();
}

// Returns a message when the browser would block this endpoint, or null when it
// would not. A message, not a boolean: whoever hits this is reading it at 2am
// in a console and needs to be told what to change, not that something is false.
export function mixedContentProblem(endpoint, pageProtocol) {
    if (typeof endpoint !== 'string' || !endpoint) return null;
    // A relative endpoint inherits the page's scheme and host, which is exactly
    // why it is the right answer when the API is behind the same proxy.
    if (!/^https?:\/\//i.test(endpoint)) return null;
    if (!/^http:\/\//i.test(endpoint)) return null;
    if (pageProtocol !== 'https:') return null;
    if (LOCAL_HOSTS.includes(hostOf(endpoint))) return null;

    return (
        `[boot] VITE_API_ENDPOINT apunta a ${endpoint}, que es http, y el panel se está `
        + 'sirviendo por https. El navegador va a bloquear TODAS las llamadas como contenido '
        + 'mixto y la pantalla se va a quedar esperando sin error visible.\n\n'
        + 'Si la API vive detrás del mismo proxy que el panel —que es el caso de este repo, '
        + 'nginx redirige /api/ al backend— el valor correcto es la ruta relativa `/api/v1`: '
        + 'hereda el esquema y el host de la página, así que no hay un `http` que se pueda '
        + 'quedar viejo. Si de verdad está en otro origen, tiene que ser `https://`.'
    );
}
